"use client";
import React, { useEffect, useRef, useState } from "react";
import NewsCard from "../components/see-insights";
import { useQuery } from "@tanstack/react-query";
import {
    cancelUnlistedTransaction,
    getPendingTransactions,
    getPriceDiff,
    getProductByISIN,
} from "@/app/api/service/unlistedstocks";
import { UEListData } from "@/app/interface/homepage";
import { TotalPaymentResponse, ISINPrices } from "@/app/interface/assetpage";
import LoadingIcon from "@/app/assets/icons/loading";
import FinancialMetrics from "@/app/alts/unlisted-shares/components/fundamental-sheet";
import Faq from "@/app/components/faq";
import AssetInformation from "@/app/alts/unlisted-shares/components/asset-information";
import { Info } from "lucide-react";
import AppStore from "@/app/assets/icons/app-store";
import PlayStore from "@/app/assets/icons/play-store";
import ScrollableButton from "@/app/alts/unlisted-shares/components/scrollable-button";
import { TransactionFlowProvider, useTransactionFlow } from "@/context/TransactionContext";
import TransactionFlow from "@/app/components/payment";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { ThirdPartyData } from "@/app/interface/docvault";
import { useSearchParams, useRouter } from "next/navigation";
import { buildKycRedirectUrl } from "@/app/utils/kyc-routing";

import {
    getPans,
    getThirdPartyUserData,
    getUserEmail,
    postUserCheckpointsStatus,
} from "@/app/api/service/user";
import {
    getAllISIN,
    getISINTransactions,
    getPortfolioByISIN,
    getPrices,
    getLastPopularStocks,
    postISINTransaction,
    postUserIntent,
    getuserStates,
} from "@/app/api/service/stocks";
import { ACTIONS } from "@/app/components/payment/actions";
import { formatIndianCurrency, scrollToTop } from "@/app/utils/helper";
import ErrorModal from "@/app/components/popup/error";
import { UserPortfolio } from "../components/user-portfolio";
import InfoCard, { InfoCardType } from "@/app/components/info-banner";
import { RecentTransactions } from "@/app/alts/unlisted-shares/components/recent-transctions";
import StaggeringPrice from "../components/staggering-table";
import { TransactionStatus, KYCStatus, BannerType } from "@/app/enums/banner";
import SuccessModal from "@/app/components/popup/success";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { initBranch, trackEvent } from "@/app/utils/branchio";
import { AboutBasket } from "../components/about-basket";
import { BasketCard } from "../components/basket-card";
import { BasketBreakup } from "../components/basket-breakup";
import useMobile from "@/app/hooks/use-mobile";
import moment from "moment";
import Image from "next/image";
import { HighRiskConsent } from "../components/high-risk-consent";
import BackNavigationHandler from "../components/back-navigation";
import AssetpageSkeleton from "@/app/components/loaders/assetpage-Skeleton";
import { IntentUserStates } from "@/app/interface/user";
import { DirectPaths } from "@/config/routes";
import { useAuth } from "@/context/authcontext";
import BannerpageSkeleton from "@/app/components/loaders/banner-skeleton";
import PendingTransactionWeb from "@/app/components/popup/pending-transaction-web";
import CancelOrdersConfirmationModal from "@/app/components/popup/cancel-orders-confirmation-modal";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { toUnlistedShareSlug } from "@/app/alts/unlisted-shares/[...id]/slug";
import { BLOCKED_SHARE_STATES, ProductVisibility } from "@/app/utils/product-visibility";

export interface PendingOrders {
    orderId: string;
    name: string;
    date: string;
    amount: string;
    mode: string;
}

type StaggeringPricesData = [number, number, number, number];

type BannerState = {
    type: BannerType;
    text: string;
    variant: InfoCardType;
    action?: { label: string; onClick: () => void };
} | null;

type UserStates = {
    obUserApproved: boolean;
    obUserNotInvested: boolean;
    obOrderApproved: boolean;
    obOrderRejected: boolean;
    obPaymentDone: boolean;
    obOrderAttempted: boolean;
};

export interface StaggeringData {
    qty: string;
    price: number;
}

interface TPDOnboardingStatus {
    noThirdPartyDataAdded: boolean;
    partialThirdPartyDataAdded: boolean;
    anyDocUploadedForThirdParty: boolean;
    allDocUploadedForThirdParty: boolean;
    noDocUploadedForThirdParty: boolean;
    productWiseAccountStatus: boolean;
}

interface PageClientContentProps {
    initialStockDetails: UEListData | null;
    productId: string | null;
    productName: string | null;
}

function PageContentInner({
    initialStockDetails,
    productId /* , productName */,
}: PageClientContentProps) {
    const router = useRouter();
    const { dispatch } = useTransactionFlow();
    const { isMobile } = useMobile();
    const { isAuthenticated, isAuthFetched } = useAuth();
    const searchParams = useSearchParams();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const [minUnits, setMinUnits] = useState<number>(initialStockDetails?.minUnits ?? 0);
    const [units, setUnits] = useState<number>(0);
    const [isKycDone, setisKycDone] = useState<boolean>(false);
    const [isLoading, setisLoading] = useState<boolean>(!initialStockDetails);
    const [isUser, setisUser] = useState(false);
    const [thirdPartyData, setthirdPartyData] = useState<ThirdPartyData>();
    const buildStockKycRedirectUrl = (currentPath: string, withBaseUrl = false) =>
        buildKycRedirectUrl({
            kycFlow: thirdPartyData?.kycFlow,
            redirectUrl: currentPath,
            queryParams: { units, isin: stockDetails?.ISIN },
            baseUrl: withBaseUrl ? process.env.ICM_URL : undefined,
        });
    const [isPurchaseStart, setisPurchaseStart] = useState<boolean>(false);
    const [isTxnJourney, setisTxnJourney] = useState<boolean>(false);
    const [unitsPrice, setUnitsPrice] = useState<ISINPrices>();
    const [priceDiff, setPriceDiff] = useState<number | null>(null);
    const [perDiff, setPerDiff] = useState<number | null>(null);
    const [timePeriod, setTimePeriod] = useState<string>("1_year");
    const [totalPayment, setTotalPayment] = useState<TotalPaymentResponse | null>(null);
    const [activeTab, setActiveTab] = useState<string>("Fundamentals");
    const [stockDetails, setstockDetails] = useState<UEListData | null>(initialStockDetails);
    const [errorStr, seterrorStr] = useState<string>();
    const [successStr, setsuccessStr] = useState<string>();
    const [isError, setisError] = useState<boolean>(false);
    const [isSuccess, setisSuccess] = useState<boolean>(false);
    const [userNotInvested, setUserNotInvested] = useState<boolean>(false);
    const [userState, setuserState] = useState<UserStates | null>(null);
    const [tpdOnBoardingData, settpdOnBoardingData] = useState<TPDOnboardingStatus>();
    const [openStaggeringTable, setopenStaggeringTable] = useState<boolean>(false);
    const [staggeringPrices, setstaggeringPrices] = useState<StaggeringData[]>([]);
    const [bannerState, setBannerState] = useState<BannerState>(null);
    const [basketPrices, setbasketPrices] = useState<ISINPrices[]>();
    const [mobileTableOpened, setmobileTableOpened] = useState<boolean>(false);
    const [isMobileWidgetOpen, setisMobileWidgetOpen] = useState<boolean>(false);
    const [isAllDataFetched, setisAllDataFetched] = useState<boolean>(false);
    const [riskConsent, setriskConsent] = useState<boolean>(false);
    const [consentAccepted, setconsentAccepted] = useState<boolean>(false);
    const [isPopularStock, setisPopularStock] = useState<UEListData | null>(null);
    const [pendingOrders, setPendingOrders] = useState<PendingOrders[] | null>(null);
    const [PendingOrdersPopup, setPendingOrdersPopup] = useState<boolean>(false);
    const [cancelOrdersConfirmation, setcancelOrderConfirmation] = useState<boolean>(false);
    const [cancelOrderConsent, setcancelOrderConfirmationConsent] = useState<boolean>(false);
    const [isIntentRecording, setIsIntentRecording] = useState<boolean>(false);
    const [hasAutoRecordedIntent, setHasAutoRecordedIntent] = useState<boolean>(false);

    const RecentTxnsRef = useRef<HTMLDivElement | null>(null);
    const WidgetRef = useRef<HTMLDivElement | null>(null);
    const txnBtn = useRef<HTMLButtonElement | null>(null);

    if (
        isAuthenticated &&
        initialStockDetails &&
        initialStockDetails?.config?.showOnPostLogin === false
    ) {
        router.replace(DirectPaths.UNLISTED_SHARES);
    }

    if (
        isAuthenticated && 
        stockDetails && 
        stockDetails?.config?.showOnPostLogin === false
    ) {
        router.replace(DirectPaths.UNLISTED_SHARES);
    }

    useEffect(() => {
        const ref = searchParams.get("ref");
        if (ref) {
            window.location.href = `${process.env.ICM_UE_URL}${DirectPaths.PLATFORM}`;
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const removeHash = () => {
            if (window.location.hash) {
                window.history.replaceState(
                    null,
                    "",
                    window.location.pathname + window.location.search,
                );
            }
        };

        removeHash();
        window.addEventListener("hashchange", removeHash);
        return () => window.removeEventListener("hashchange", removeHash);
    }, []);

    const handlePendingOrdersPopup = () => {
        setPendingOrdersPopup(!PendingOrdersPopup);
        setcancelOrderConfirmationConsent(true);
    };

    const cancelPendingOrder = async (): Promise<boolean> => {
        const result = await cancelUnlistedTransaction();
        if (result.success) {
            setPendingOrders([]);
            setPendingOrdersPopup(false);
            setcancelOrderConfirmation(true);
            return true;
        } else {
            return false;
        }
    };

    const closeCancelOrdersConfirmationModal = () => {
        if (cancelOrdersConfirmation) {
            setcancelOrderConfirmationConsent(true);
            setcancelOrderConfirmation(false);
        } else setcancelOrderConfirmation(true);
    };

    // Function to update banner for transactions
    const updateBannerForTransaction = () => {
        let text = "";
        let action;
        let variant = InfoCardType.INFO;

        switch (true) {
            case !tpdOnBoardingData?.allDocUploadedForThirdParty:
                text = KYCStatus.STARTKYC;
                variant = InfoCardType.INFO;
                action = {
                    label: "Click here to complete now.",
                    onClick: () => {
                        const currentPath = window.location.pathname + window.location.search;
                        setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                        cleverTapTrackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`, {});
                        // initBranch()
                        //     .then(() => {
                        //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                        //     })
                        //     .catch((error) => console.error("Error initializing Branch:", error));
                        router.push(buildStockKycRedirectUrl(currentPath));
                    },
                };
                setBannerState({ type: BannerType.KYC, variant, text, action });
                break;

            case userState?.obUserApproved:
                if (
                    userState?.obOrderAttempted &&
                    !userState?.obPaymentDone &&
                    !stockDetails?.userInRfqCug
                ) {
                    text = TransactionStatus.FAILED;
                    variant = InfoCardType.ERROR;
                    action = {
                        label: "Click here to place a new order.",
                        onClick: () => {
                            if (WidgetRef.current && !isMobile) {
                                WidgetRef.current.scrollIntoView({ behavior: "smooth" });
                            } else if (txnBtn.current) {
                                txnBtn.current.click();
                            }
                            setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                            cleverTapTrackEvent(
                                `US_Asset_Page_Banner_${stockDetails?.product}`,
                                {},
                            );
                            // initBranch()
                            //     .then(() => {
                            //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                            //     })
                            //     .catch((error) =>
                            //         console.error("Error initializing Branch:", error),
                            //     );
                        },
                    };
                }

                if (
                    userState?.obPaymentDone &&
                    !userState?.obOrderApproved &&
                    !userState?.obOrderRejected
                ) {
                    text = TransactionStatus.PENDING;
                    variant = InfoCardType.INFO;
                    action = {
                        label: "Click here to view details.",
                        onClick: () => {
                            if (RecentTxnsRef.current) {
                                RecentTxnsRef.current.scrollIntoView({ behavior: "smooth" });
                            }
                            setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                            cleverTapTrackEvent(
                                `US_Asset_Page_Banner_${stockDetails?.product}`,
                                {},
                            );
                            // initBranch()
                            //     .then(() => {
                            //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                            //     })
                            //     .catch((error) =>
                            //         console.error("Error initializing Branch:", error),
                            //     );
                        },
                    };
                }

                if (userState?.obPaymentDone && userState?.obOrderApproved) {
                    text = `${TransactionStatus.SUCCESS} ${stockDetails?.settlementDays?.value ? stockDetails?.settlementDays?.value : "3"} working days.`;
                    variant = InfoCardType.SUCCESS;
                    action = {
                        label: "Click here to view details.",
                        onClick: () => {
                            if (RecentTxnsRef.current) {
                                RecentTxnsRef.current.scrollIntoView({ behavior: "smooth" });
                            }
                            setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                            cleverTapTrackEvent(
                                `US_Asset_Page_Banner_${stockDetails?.product}`,
                                {},
                            );
                            // initBranch()
                            //     .then(() => {
                            //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                            //     })
                            //     .catch((error) =>
                            //         console.error("Error initializing Branch:", error),
                            //     );
                        },
                    };
                }

                if (userState?.obPaymentDone && userState?.obOrderRejected) {
                    text = TransactionStatus.REJECTED;
                    variant = InfoCardType.ERROR;
                    action = {
                        label: "Click here to place a new order.",
                        onClick: () => {
                            if (WidgetRef.current && !isMobile) {
                                WidgetRef.current.scrollIntoView({ behavior: "smooth" });
                            } else if (txnBtn.current) {
                                txnBtn.current.click();
                            }
                            setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                            cleverTapTrackEvent(
                                `US_Asset_Page_Banner_${stockDetails?.product}`,
                                {},
                            );
                            // initBranch()
                            //     .then(() => {
                            //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                            //     })
                            //     .catch((error) =>
                            //         console.error("Error initializing Branch:", error),
                            //     );
                        },
                    };
                }

                if (userState?.obUserNotInvested) {
                    text = KYCStatus.COMPLETE;
                    variant = InfoCardType.SUCCESS;
                    action = {
                        label: "Click here to Invest Now.",
                        onClick: () => {
                            if (WidgetRef.current) {
                                WidgetRef.current.scrollIntoView({ behavior: "smooth" });
                            }
                            setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                            cleverTapTrackEvent(
                                `US_Asset_Page_Banner_${stockDetails?.product}`,
                                {},
                            );
                            // initBranch()
                            //     .then(() => {
                            //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                            //     })
                            //     .catch((error) =>
                            //         console.error("Error initializing Branch:", error),
                            //     );
                        },
                    };
                }
                setBannerState({ type: BannerType.KYC, variant, text, action });
                break;

            case !userState?.obUserApproved:
                if (kycErrors?.data[0]?.rejectRemarks.length > 0) {
                    text = KYCStatus.REINITIATE;
                    variant = InfoCardType.ERROR;
                    action = {
                        label: "Click here to Re-initiate KYC.",
                        onClick: () => {
                            const currentPath = window.location.pathname + window.location.search;
                            setAnalyticsEvent("", `US_Asset_Page_Banner_${stockDetails?.product}`);
                            cleverTapTrackEvent(
                                `US_Asset_Page_Banner_${stockDetails?.product}`,
                                {},
                            );
                            // initBranch()
                            //     .then(() => {
                            //         trackEvent(`US_Asset_Page_Banner_${stockDetails?.product}`);
                            //     })
                            //     .catch((error) =>
                            //         console.error("Error initializing Branch:", error),
                            //     );
                            router.push(buildStockKycRedirectUrl(currentPath));
                        },
                    };
                } else {
                    text = KYCStatus.DONE;
                    variant = InfoCardType.INFO;
                }
                setBannerState({ type: BannerType.KYC, variant, text, action });
                break;
            default:
                setBannerState(null);
        }
    };

    const { data: newUserStates, isFetched: isNewUserStateFetched } = useQuery({
        queryKey: ["userStates"],
        queryFn: () => getuserStates(),
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const { data: popularStocks, isFetched: isPopularStocksFetched } = useQuery({
        queryKey: ["popularStocks"],
        queryFn: getLastPopularStocks,
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const { data: userData, isFetched: isUserFetched } = useQuery({
        queryKey: ["userData"],
        queryFn: getUserEmail,
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const { data: kycErrors, isFetched: isKycDataFetched } = useQuery({
        queryKey: ["kycData"],
        queryFn: getPans,
        enabled: !!userData,
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const shouldFetchIsinsForMapping = !initialStockDetails;

    const { data: isinsData, isFetched: isIsinsFetched } = useQuery({
        queryKey: ["isinsData"],
        queryFn: getAllISIN,
        enabled: shouldFetchIsinsForMapping,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const {
        data: pendingTxns,
        isFetched: isPendingTxnsFetched,
        refetch: refetchPendingTxns,
    } = useQuery({
        queryKey: ["pendingTxns"],
        queryFn: getPendingTransactions,
        enabled: !!userData,
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });
    
    const matchedISIN = initialStockDetails?.ISIN
        ? initialStockDetails.ISIN
        : isIsinsFetched
          ? isinsData?.data?.find(
                (item: UEListData) =>
                    item?.product === productId ||
                    item?.company?.displayName?.toLowerCase().split(" ").join("-") ===
                        productId?.toLowerCase().split(" ").join("-"),
            )?.ISIN
          : null;

    // If SSR couldn't resolve the product (e.g. auth-only product on a public
    // SSR fetch) and the client-side ISIN list (which sends user cookies via
    // `credentials: "include"`) also can't find it after auth has resolved,
    // the product is genuinely not available to this user — show a 404 page
    // instead of crashing the route via an error boundary.
    if (!initialStockDetails && isIsinsFetched && !matchedISIN && isAuthFetched) {
        // notFound();
        console.log("##11: ", "initialStockDetails", "isIsinsFetched", "matchedISIN", "isAuthFetched");
        console.log("##11: ", initialStockDetails, isIsinsFetched, matchedISIN, isAuthFetched);
        router.replace(DirectPaths.UNLISTED_SHARES);
    }

    const { data: stockData, isFetched: isStockFetched } = useQuery({
        queryKey: ["stockData", matchedISIN],
        queryFn: () => getProductByISIN(matchedISIN?.ISIN ?? matchedISIN),
        enabled: !!matchedISIN && !initialStockDetails,
        initialData: initialStockDetails ? { data: initialStockDetails, success: true } : undefined,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const { data: tpdData, isFetched: isTPDFetched } = useQuery({
        queryKey: ["tpdData"],
        queryFn: getThirdPartyUserData,
        enabled: !!userData,
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const {
        data: txnData,
        isFetched: isTxnDataFetched,
        refetch: refetchTxnData,
    } = useQuery({
        queryKey: ["txnData", stockDetails?.ISIN],
        queryFn: () => getISINTransactions({ isin: stockDetails?.ISIN ?? "" }),
        enabled: !!userData && !!stockDetails?.ISIN,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const { data: portfolioData } = useQuery({
        queryKey: ["portfolioData"],
        queryFn: () => getPortfolioByISIN({ isin: stockDetails?.ISIN ?? "" }),
        enabled: !!userData && !!stockDetails?.ISIN,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const { data: stockDataClient, isFetched: isStockDataClientFetched } = useQuery({
        queryKey: ["stockDataClient", matchedISIN],
        queryFn: () => getProductByISIN(matchedISIN?.ISIN ?? matchedISIN),
        enabled: isAllDataFetched,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (isStockDataClientFetched && stockDataClient?.success) {
            const details = stockDataClient.data;
            setMinUnits(details.minUnits ?? 0);
        }
    }, [isStockDataClientFetched]);

    // Keep the document metadata update for authenticated users.
    // URL canonicalization is handled server-side via permanent redirects.
    useEffect(() => {
        if (isAuthenticated) {
            const meta_noindex = document.createElement("meat");
            meta_noindex.setAttribute("name", "robots");
            meta_noindex.setAttribute("content", "noindex");
            document.head.appendChild(meta_noindex);
        }

        if (!isStockFetched || !stockData?.data) return;

        const companySlug = toUnlistedShareSlug(stockData?.data?.company?.displayName ?? "");
        if (typeof window != "undefined" && isAuthenticated) {
            const msg = stockData?.data?.company?.displayName + " " + window.document.title;
            const meta_des = [...window.document.querySelectorAll("meta")].filter(
                (item) => item.name === "description",
            )?.[0];
            const meta_keyword = [...window.document.querySelectorAll("meta")].filter(
                (item) => item.name === "keywords",
            )?.[0];
            const link_canonical = [...window.document.querySelectorAll("link")].filter(
                (item) => item.rel === "canonical",
            )[0];

            window.document.title = msg;
            if (meta_des) {
                meta_des.setAttribute("content", msg);
            }
            if (meta_keyword) {
                meta_keyword.setAttribute(
                    "content",
                    stockData?.data?.company?.displayName + " - " + meta_keyword.content,
                );
            }
            if (link_canonical) {
                link_canonical.setAttribute("href", link_canonical.href + "/" + companySlug);
            }
        }
    }, [isAuthenticated, stockData, isStockFetched]);

    useEffect(() => {
        if (isPopularStocksFetched && popularStocks?.data?.length > 0) {
            const matchedStock = popularStocks?.data?.find(
                (item: UEListData) => item?.product === productId,
            );

            setisPopularStock(matchedStock);
        }
    }, [isPopularStocksFetched]);

    useEffect(() => {
        if (isPendingTxnsFetched && pendingTxns?.data?.length > 0) {
            const ordersList = pendingTxns?.data?.map((order: UEListData) => ({
                orderId: order?.orderId,
                name: order?.displayName,
                date: order?.date,
                amount: formatIndianCurrency(order?.amount),
                mode: order?.paymentType,
            }));

            setPendingOrders(ordersList);
        }
    }, [isPendingTxnsFetched, pendingTxns]);

    useEffect(() => {
        scrollToTop();
    }, []);

    useEffect(() => {
        if (
            [
                portfolioData,
                isKycDone,
                userNotInvested,
                tpdData,
                txnData,
                isKycDataFetched,
                userState,
                tpdOnBoardingData,
                stockDetails,
                kycErrors,
                txnData,
            ].every((item) => item !== null)
        ) {
            setisAllDataFetched(true);
            updateBannerForTransaction();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tpdData,
        txnData,
        isKycDataFetched,
        userNotInvested,
        kycErrors,
        isKycDone,
        userState,
        portfolioData,
        stockDetails,
        tpdOnBoardingData,
    ]);

    function convertStaggeringData(data: StaggeringPricesData[]): StaggeringData[] {
        return data.map(([start, end, , price]) => ({
            qty: `${start} - ${end}`,
            price: price,
        }));
    }

    useEffect(() => {
        if ((isStockFetched && stockData?.success) || initialStockDetails) {
            const details = stockData?.data || initialStockDetails;
            setstockDetails(details);
            if (details?.isBasket) {
                details?.basketConstituents?.forEach((stock: UEListData) => {
                    getPrices({ isin: stock.ISIN, units: 1 }).then((res) => {
                        if (!res.success) {
                            console.error("Unable to fetch price");
                        } else {
                            setbasketPrices((prevState) => [...(prevState ?? []), res.data]);
                        }
                    });
                });
            }
            setisLoading(false);
        } else {
            setisLoading(false);
        }
    }, [
        isStockFetched,
        stockData,
        stockDetails,
        productId,
        isinsData,
        isIsinsFetched,
        matchedISIN,
        initialStockDetails,
    ]);

    // TODO: Centralize the flow for user verification for logged in state
    useEffect(() => {
        if (isUserFetched && userData) {
            setisUser(true);
        } else {
            setisUser(false);
        }
    }, [isUserFetched, userData]);

    useEffect(() => {
        if (tpdData && stockDetails) {
            postUserCheckpointsStatus(
                tpdData.data?.email,
                tpdData.data?.pan,
                [
                    "obUserAdminResponseDone",
                    "obUserApproved",
                    "obUserNotInvested",
                    "obOrderAttempted",
                    "obDocSigned",
                    "obPaymentDone",
                    "obOrderApproved",
                    "obOrderRejected",
                ],
                "obUserTxn",
                stockDetails?.product ?? "orobonds",
                "unlistedEquities",
            )
                .then((response) => {
                    if (!response.success) {
                        console.error("Error posting user checkpoints status:", response.message);
                    } else {
                        setuserState(response?.results);
                        setUserNotInvested(response?.results?.obUserNotInvested);
                        setisKycDone(response?.results?.obUserApproved);
                    }
                })
                .catch((error) => {
                    console.error(
                        "An error occurred while posting user checkpoints status:",
                        error,
                    );
                });

            postUserCheckpointsStatus(
                tpdData.data?.email,
                tpdData.data?.pan,
                [
                    "noThirdPartyDataAdded",
                    "partialThirdPartyDataAdded",
                    "anyDocUploadedForThirdParty",
                    "allDocUploadedForThirdParty",
                    "noDocUploadedForThirdParty",
                    "productWiseAccountStatus",
                ],
                "thirdPartyOnboarding",
                "orobonds",
            )
                .then((response) => {
                    if (!response.success) {
                        console.error("Error posting user checkpoints status:", response.message);
                    } else {
                        settpdOnBoardingData(response?.results);
                    }
                })
                .catch((error) => {
                    console.error(
                        "An error occurred while posting user checkpoints status:",
                        error,
                    );
                });
        }
    }, [tpdData, stockDetails]);

    useEffect(() => {
        if (isTPDFetched && tpdData) {
            setthirdPartyData(tpdData?.data);
        }
    }, [isTPDFetched, tpdData]);

    const decreaseUnits = () => {
        if (units > (minUnits ?? stockDetails?.minUnits ?? 0)) {
            setUnits(units - 1);
        }
    };

    const handleUnitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            const numericValue = value === "" ? 0 : Number(value);
            if (numericValue <= (stockDetails?.maxUnits ?? Number.MAX_SAFE_INTEGER)) {
                setUnits(numericValue);
            }
        }
    };

    const handleIncreaseUnits = () => {
        if (units < (stockDetails?.maxUnits ?? Number.MAX_SAFE_INTEGER)) {
            setUnits(units + 1);
        }
    };

    const intentNow = async () => {
        if (!isAuthenticated) {
            const currentPath = window.location.pathname + window.location.search;
            const redirectUrl = `${currentPath}${currentPath.includes("?") ? "&" : "?"}recordIntent=true`;

            router.push(`/login?redirect=${redirectUrl}`);
            return;
        }

        setIsIntentRecording(true);
        try {
            const payload = {
                units: minUnits ?? stockDetails?.minUnits ?? 1,
                amount: unitsPrice?.benefits
                    ? unitsPrice?.benefits?.total?.withBenefitAmount
                    : (unitsPrice?.amount ?? 0),
                status: true,
                productType: stockDetails?.productType ?? "",
            };

            const result = await postUserIntent({ isin: stockDetails?.ISIN ?? "", payload });

            if (result) {
                setsuccessStr(result?.message);
                setisSuccess(true);
            } else {
                seterrorStr(result?.message);
                setisError(true);
            }
            setisPurchaseStart(false);

            setAnalyticsEvent("", `US_Asset_Page_Intent_${stockDetails?.product}`);
            cleverTapTrackEvent(`US_Asset_Page_Intent_${stockDetails?.product}`, {});
        } finally {
            setIsIntentRecording(false);
        }
        // initBranch()
        //     .then(() => {
        //         trackEvent(`US_Asset_Page_Intent_${stockDetails?.product}`);
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));
    };

    useEffect(() => {
        const shouldRecordIntent = searchParams.get("recordIntent") === "true";

        if (shouldRecordIntent && isAuthenticated && !hasAutoRecordedIntent && !isIntentRecording) {
            setHasAutoRecordedIntent(true);

            // Remove recordIntent flag from URL after using it
            if (typeof window !== "undefined") {
                const url = new URL(window.location.href);
                url.searchParams.delete("recordIntent");
                window.history.replaceState(null, "", url.toString());
            }

            void intentNow();
        }
    }, [searchParams, isAuthenticated, hasAutoRecordedIntent, isIntentRecording]);

    useEffect(() => {
        if (minUnits || stockDetails?.minUnits) {
            setUnits(minUnits || stockDetails?.minUnits || 0); // Set initial units to minUnits
        }
    }, [minUnits, stockDetails?.minUnits]);

    useEffect(() => {
        let time = null;
        switch (timePeriod) {
            case "1_year":
                time = 365;
                break;
            case "1_month":
                time = 30;
                break;
            case "3_months":
                time = 90;
                break;
            case "2_years":
                time = 730;
                break;
            case "3_years":
                time = 1095;
                break;
            default:
                time = 365;
                break;
        }

        if (stockDetails?.ISIN) {
            getPriceDiff(stockDetails?.ISIN, time)
                .then((response) => {
                    if (!response.success) {
                        // return console.error(response.message);
                    }
                    setPriceDiff(response?.data?.priceDiff);
                    setPerDiff(response?.data?.percDiff);
                })
                .catch((error) => {
                    console.error("An error occurred while fetching price differences. : ", error);
                });
        }
    }, [stockDetails?.ISIN, timePeriod]);

    useEffect(() => {
        if (stockDetails?.ISIN && units >= minUnits) {
            getPrices({ isin: stockDetails?.ISIN, units }).then((res) => {
                if (res && res.data && res.success) {
                    setUnitsPrice(res.data || { unitPrice: 0 });
                    setTotalPayment(res.data);
                    if (res.data?.benefits?.staggeredPrice?.priceLadder?.length > 0) {
                        const result = convertStaggeringData(
                            res.data?.benefits?.staggeredPrice?.priceLadder,
                        );
                        setstaggeringPrices(result);
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units, stockDetails?.ISIN]);

    useEffect(() => {
        if (stockDetails?.riskCategory?.consentRequired && consentAccepted) {
            setriskConsent(false);
            startTransaction();
        }
    }, [consentAccepted]);

    const startTransaction = async () => {
        setisPurchaseStart(true);
        if (!userData) {
            router.push(
                `${process.env.ICM_UE_POST_URL}/login?redirect=${DirectPaths.UNLISTED_SHARES}/${stockDetails?.product}`,
            );
        } else if (!isKycDone || !thirdPartyData?.pan) {
            if (userNotInvested && !isKycDone) {
                initBranch()
                    .then(() =>
                        trackEvent(
                            "US_GetStarted_Click",
                            thirdPartyData?.email || "",
                            thirdPartyData?.phone || "",
                            userData?.userId || "",
                        ),
                    )
                    .catch((error) => console.error("Error initializing Branch:", error));
                cleverTapTrackEvent("US_GetStarted_Click", {
                    email: thirdPartyData?.email || "",
                    phone: thirdPartyData?.phone || "",
                    userId: userData?.userId || "",
                    product: stockDetails?.product || "",
                });
            }
            const currentPath = window.location.pathname + window.location.search;
            router.push(buildStockKycRedirectUrl(currentPath, true));
        } else if (pendingOrders && pendingOrders.length > 0 && !cancelOrderConsent) {
            if (stockDetails?.riskCategory?.consentRequired && !consentAccepted) {
                setriskConsent(true);
            } else {
                setPendingOrdersPopup(true);
                setisPurchaseStart(false);
            }
        } else if (stockDetails && !stockDetails?.isProductAvailable) {
            setAnalyticsEvent("", `US_Asset_Page_Intent_${stockDetails?.product}`);
            // initBranch()
            //     .then(() => trackEvent(`US_Asset_Page_Intent_${stockDetails?.product}`))
            //     .catch((error) => console.error("Error initializing Branch:", error));

            intentNow();
        } else if (tpdData && isUser) {
            if (stockDetails?.riskCategory?.consentRequired && !consentAccepted) {
                setisMobileWidgetOpen(false);
                setisPurchaseStart(false);
                setriskConsent(true);
                return;
            }

            setAnalyticsEvent("", `US_Asset_Page_Invest_Now_${stockDetails?.product}`);
            cleverTapTrackEvent(`US_Asset_Page_Invest_Now_${stockDetails?.product}`, {});
            // initBranch()
            //     .then(() => {
            //         trackEvent(`US_Asset_Page_Invest_Now_${stockDetails?.product}`);
            //     })
            //     .catch((error) => console.error("Error initializing Branch:", error));
            const result = await postISINTransaction({
                isin: stockDetails?.ISIN ?? "",
                payload: {
                    units: units,
                    tradeDate: new Date().toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                    }),
                    txnType: "purchase",
                    subPartner: "Incred",
                },
            });

            if (result.success) {
                if (result && result.data.orderId && result.data.paymentType === null) {
                    setisTxnJourney(true);
                    dispatch({
                        type: ACTIONS.SET_DIGIO_FLOW,
                        payload: {
                            orderId: result.data.orderId,
                            paymentType: null,
                            allowedTxnType: result?.data?.allowedPaymentTypes,
                            NeftDetails: result?.data?.receivers
                                ? result?.data?.receivers[0]
                                : null,
                        },
                    });
                }
            } else {
                setisTxnJourney(false);
                setisPurchaseStart(false);
                seterrorStr(result?.message);
                setisError(true);
            }
        } else {
            setisPurchaseStart(true);
            router.push("/login");
        }
    };

    const handleComplete = () => {
        refetchPendingTxns();
        setcancelOrderConfirmationConsent(false);
        setconsentAccepted(false);
        setisTxnJourney(false);
        setisPurchaseStart(false);
        refetchTxnData();
    };

    const getBtnTxt = () => {
        if (kycErrors && kycErrors?.data[0]?.rejectRemarks.length > 0) return "Re-initiate KYC";
        if (!isUser) return "Invest Now";
        if (userNotInvested && kycErrors?.data[0]?.rejectRemarks.length === 0 && !isKycDone)
            return "Submit KYC";
        if (userNotInvested && !isKycDone) return "Get Started";
        if (!isKycDone) return "Complete KYC";
        if (stockDetails && !stockDetails?.isProductAvailable) return "Notify Me";
        return "Invest Now";
    };

    const isinMap = new Map(
        stockDetails?.basketConstituents?.map(({ ISIN, company }) => [ISIN, company?.displayName]),
    );

    const basketBreakup = unitsPrice?.distribution
        ? unitsPrice?.distribution.map(({ ISIN, units, amount }) => ({
              companyName: isinMap.get(ISIN) || "Unknown",
              ISIN,
              units,
              amount,
          }))
        : [];

    useEffect(() => {
        if (cancelOrderConsent) {
            txnBtn.current?.click();
        }
    }, [cancelOrderConsent]);

    return (
        <>
            {!isLoading && stockDetails ? (
                <>
                    {isAuthenticated &&
                        (!isAllDataFetched ? (
                            <BannerpageSkeleton />
                        ) : [
                              portfolioData,
                              isKycDone,
                              userNotInvested,
                              tpdData,
                              txnData,
                              isKycDataFetched,
                              userState,
                              tpdOnBoardingData,
                              stockDetails,
                              kycErrors,
                              txnData,
                          ].every((item) => item !== null) && bannerState?.text ? (
                            <InfoCard
                                title={bannerState.text}
                                linkText={bannerState.action?.label ?? ""}
                                func={bannerState.action?.onClick ?? (() => {})}
                                type={bannerState.variant}
                            />
                        ) : null)}

                    {portfolioData?.data && portfolioData?.data?.cumulative?.amountInvested > 0 ? (
                        <UserPortfolio data={portfolioData?.data?.cumulative} />
                    ) : null}
                    <div className="flex flex-col bg-white px-6 py-0 sm:container sm:py-6 md:flex-row md:justify-between md:gap-[40px]">
                        <div className="max-w-[812px] flex-col">
                            {stockDetails && !stockDetails.isBasket ? (
                                <AssetInformation
                                    assetDetails={stockDetails}
                                    perDiff={perDiff}
                                    priceDiff={priceDiff}
                                    timePeriod={timePeriod}
                                    totalPayment={totalPayment}
                                    setTimePeriod={setTimePeriod}
                                    unitPrice={unitsPrice}
                                    isMobileWidgetOpen={isMobileWidgetOpen}
                                />
                            ) : (
                                <>
                                    <AboutBasket
                                        basketDetails={stockDetails?.basketConstituents ?? null}
                                    />
                                    {stockDetails?.isBasket && (
                                        <div className="-mt-10 block md:hidden">
                                            <BasketBreakup BasketData={basketBreakup} />
                                        </div>
                                    )}
                                    {stockDetails?.basketConstituents?.map((stock, index) => {
                                        return (
                                            <BasketCard
                                                stock={stock}
                                                basketPrices={basketPrices ?? []}
                                                key={index}
                                            />
                                        );
                                    })}
                                </>
                            )}
                            <div className="mb-6 mt-4 block md:hidden">
                                <div className="w-full space-y-6 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">
                                                Price per unit
                                            </span>
                                            <span className="text-sm font-medium">
                                                ₹{(unitsPrice?.unitPrice ?? 0).toLocaleString()}
                                            </span>
                                        </div>

                                        {stockDetails?.settlementDays?.isShow && (
                                            <div className="flex h-[38px] items-center justify-between">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-sm text-gray-600">
                                                        Settlement period
                                                    </span>
                                                    <div className="group relative">
                                                        <Info
                                                            width="16"
                                                            height="16"
                                                            className="text-neutral-300"
                                                        />
                                                        <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                            For NEFT Payment mode,the exact
                                                            settlement date may vary.
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                                    {stockDetails?.estimatedSettlementDate
                                                        ? moment(
                                                              stockDetails?.estimatedSettlementDate,
                                                              "MM/DD/YYYY",
                                                          ).format("DD MMM YYYY")
                                                        : `T + ${stockDetails?.settlementDays?.value} days`}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">
                                                Min. units
                                            </span>
                                            <span className="text-sm">
                                                {minUnits === null ? (
                                                    <LoadingIcon className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    minUnits
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex flex-col">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">
                                                    No. of units to buy
                                                </span>
                                                <div className="flex items-center">
                                                    <button
                                                        className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-black hover:bg-neutral-200"
                                                        onClick={decreaseUnits}
                                                        disabled={units <= (minUnits || 0)}
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        className={`${String(units).length < 5 ? "w-12" : String(units).length < 7 ? "mx-3 w-14" : "mx-3 w-16"} text-center text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900 focus:outline-none`}
                                                        type="text"
                                                        value={units}
                                                        onChange={handleUnitsChange}
                                                    />
                                                    <button
                                                        className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-black hover:bg-neutral-200"
                                                        onClick={handleIncreaseUnits}
                                                        disabled={
                                                            units >=
                                                            (stockDetails?.maxUnits ??
                                                                Number.MAX_SAFE_INTEGER)
                                                        }
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                            {units < (minUnits ?? 0) && (
                                                <p className="mt-2 text-center text-sm text-brand-primary-500">
                                                    Minimum units should be{" "}
                                                    {minUnits || stockDetails?.minUnits}
                                                </p>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">
                                                Final amount
                                            </span>
                                            <span className="text-sm font-semibold">
                                                {units >= (minUnits ?? 0)
                                                    ? formatIndianCurrency(unitsPrice?.amount ?? 0)
                                                    : "₹0"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {stockDetails?.blogs?.enable && (
                                <NewsCard
                                    newsData={stockDetails?.blogs?.data}
                                    productId={stockDetails.product}
                                    thirdPartyData={thirdPartyData}
                                />
                            )}
                            {stockDetails && !stockDetails?.isBasket ? (
                                <FinancialMetrics
                                    stockDetails={stockDetails}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                />
                            ) : (
                                <></>
                            )}
                            {isTxnDataFetched && txnData?.data?.length > 0 ? (
                                <div ref={RecentTxnsRef}>
                                    <RecentTransactions
                                        orderData={txnData?.data}
                                        mobileTableUIOpened={mobileTableOpened}
                                        setmobileTableUIOpened={setmobileTableOpened}
                                    />
                                </div>
                            ) : (
                                <></>
                            )}
                            {activeTab !== "FAQs" && (
                                <Faq
                                    bgColor="bg-white"
                                    compact={true}
                                    product="others"
                                    subProduct="generic"
                                />
                            )}
                        </div>
                        <div className="sm-mb-0 mb-8 w-full sm:mt-[1] sm:max-w-[300px]">
                            <div className="sticky top-8 space-y-6 shadow-sm sm:block sm:min-h-[740px]">
                                <div
                                    className={`hidden rounded-xl border border-gray-200 bg-white shadow-lg sm:block ${!stockDetails?.isProductAvailable ? "h-[410px] border-none bg-white" : ""}`}
                                >
                                    {!stockDetails?.isProductAvailable && (
                                        <div className="absolute left-0 top-0 z-10 max-h-[740px] w-full rounded-xl bg-white px-2 opacity-95">
                                            <div className="gap 4 mt-[50px] flex flex-col items-center justify-between px-6">
                                                <Image
                                                    src={stockDetails?.company?.logoUrl ?? ""}
                                                    className="bg-white"
                                                    alt="logo"
                                                    width={162}
                                                    height={162}
                                                />
                                                <h3 className="pt-2 text-center text-xl font-semibold text-gray-900">
                                                    {stockDetails?.company?.displayName}
                                                </h3>

                                                <p className="mt-2 px-4 py-2 text-center text-sm font-medium text-black opacity-100">
                                                    Sold out. Tap Notify for updates!
                                                </p>

                                                <RegularButton
                                                    noIcon={true}
                                                    label={
                                                        isIntentRecording ? (
                                                            <LoadingIcon className="animate-spin" />
                                                        ) : (
                                                            "Notify Me"
                                                        )
                                                    }
                                                    showRightIcon={false}
                                                    variant={ButtonVariant.PrimaryDefault}
                                                    size={ButtonSize.Medium}
                                                    disabled={isIntentRecording}
                                                    onClick={() => intentNow()}
                                                    className="mt-auto min-w-full max-w-full"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="p-s9">
                                        <h3 className="text-base font-semibold text-gray-900">
                                            {stockDetails?.company?.displayName}
                                        </h3>
                                    </div>
                                    <div className="mx-s9 border-b border-dashed border-neutralOpacityDark-20"></div>
                                    <div className="md:mx-s9 md:my-s7" ref={WidgetRef}>
                                        <div className="flex h-[38px] items-center justify-between">
                                            <span className="text-sm font-regular text-neutral-500">
                                                Price per unit
                                            </span>
                                            <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                                {formatIndianCurrency(unitsPrice?.unitPrice ?? 0)}
                                            </span>
                                        </div>

                                        {stockDetails?.settlementDays?.isShow && (
                                            <div className="flex h-[38px] items-center justify-between">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-sm text-gray-600">
                                                        Settlement period
                                                    </span>
                                                    <div className="group relative">
                                                        <Info
                                                            width="16"
                                                            height="16"
                                                            className="text-neutral-300"
                                                        />
                                                        <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                            For NEFT Payment mode,the exact
                                                            settlement date may vary.
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                                    {stockDetails?.estimatedSettlementDate
                                                        ? moment(
                                                              stockDetails?.estimatedSettlementDate,
                                                              "MM/DD/YYYY",
                                                          ).format("DD MMM YYYY")
                                                        : `T + ${stockDetails?.settlementDays?.value} days`}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex h-[38px] items-center justify-between">
                                            <span className="text-sm text-gray-600">
                                                Min. units
                                            </span>
                                            <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                                {minUnits === null ? (
                                                    <LoadingIcon className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    minUnits
                                                )}
                                            </span>
                                        </div>

                                        <div
                                            className={`flex h-[38px] items-center justify-between ${staggeringPrices ? "" : "pb-4"}`}
                                        >
                                            <div className="flex w-full flex-col">
                                                <div className="flex w-full items-center justify-between">
                                                    <span
                                                        className={`${String(units).length < 5 ? "" : "flex-1"} text-sm text-gray-600`}
                                                    >
                                                        No. of units to buy
                                                    </span>

                                                    <div className="flex items-center">
                                                        <button
                                                            className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-black hover:bg-neutral-200"
                                                            onClick={decreaseUnits}
                                                            disabled={units <= (minUnits || 0)}
                                                        >
                                                            -
                                                        </button>
                                                        <input
                                                            className={`${String(units).length < 5 ? "w-12" : String(units).length < 7 ? "mx-3 w-14" : "mx-3 w-16"} text-center text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900 focus:outline-none`}
                                                            type="text"
                                                            value={units}
                                                            onChange={handleUnitsChange}
                                                        />
                                                        <button
                                                            className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-black hover:bg-neutral-200"
                                                            onClick={handleIncreaseUnits}
                                                            disabled={
                                                                units >=
                                                                (stockDetails?.maxUnits ??
                                                                    Number.MAX_SAFE_INTEGER)
                                                            }
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    {units <
                                                        (minUnits ??
                                                            stockDetails?.minUnits ??
                                                            0) && (
                                                        <p className="mb-2 text-center text-sm text-brand-primary-500">
                                                            Minimum units should be{" "}
                                                            {minUnits || stockDetails?.minUnits}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {staggeringPrices.length > 0 && (
                                            <div
                                                className="flex h-[25px] items-center justify-between pb-6 pt-3"
                                                onClick={() => {
                                                    if (userData) {
                                                        setAnalyticsEvent(
                                                            "",
                                                            `US_Asset_Page_StaggeredLink_${stockDetails?.product}`,
                                                        );
                                                        cleverTapTrackEvent(
                                                            `US_Asset_Page_StaggeredLink_${stockDetails?.product}`,
                                                            {},
                                                        );
                                                        // initBranch()
                                                        //     .then(() => {
                                                        //         trackEvent(
                                                        //             `US_Asset_Page_StaggeredLink_${stockDetails?.product}`,
                                                        //         ),
                                                        //     )
                                                        //     .catch((error) =>
                                                        //         console.error(
                                                        //             "Error initializing Branch:",
                                                        //             error,
                                                        //         ),
                                                        //     );
                                                    }
                                                    setopenStaggeringTable(true);
                                                }}
                                            >
                                                <span className="cursor-pointer text-sm font-regular text-brand-primary-500 underline underline-offset-2">
                                                    Grab this opportunity at{" "}
                                                    {formatIndianCurrency(
                                                        staggeringPrices[
                                                            staggeringPrices.length - 1
                                                        ]?.price,
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        <div className="border-b border-dashed border-neutralOpacityDark-20"></div>
                                        <div className="pt-s9">
                                            {stockDetails?.isProductAvailable && (
                                                <div className="flex h-[38px] items-center justify-between">
                                                    <span className="text-sm text-neutral-500">
                                                        Final amount
                                                    </span>
                                                    <span className="text-lg font-semibold leading-[164%] tracking-[-0.18px]">
                                                        {units >= (minUnits ?? 0)
                                                            ? formatIndianCurrency(
                                                                  unitsPrice?.amount ?? 0,
                                                              )
                                                            : "₹0"}
                                                    </span>
                                                </div>
                                            )}
                                            {(unitsPrice?.benefits?.total?.discountAmount ?? 0) >
                                                0 && stockDetails?.isProductAvailable ? (
                                                <>
                                                    <div className="flex h-[38px] items-center justify-between">
                                                        <span className="text-sm text-neutral-500">
                                                            {`Offer benefit (${unitsPrice?.benefits?.total?.discountPerc ?? "--"}%)`}
                                                        </span>
                                                        <span className="text-sm font-regular leading-[160%] tracking-[0.14px]">
                                                            {units >= (minUnits ?? 0)
                                                                ? formatIndianCurrency(
                                                                      unitsPrice?.benefits?.total
                                                                          ?.discountAmount ?? 0,
                                                                  )
                                                                : "₹0"}
                                                        </span>
                                                    </div>
                                                    {unitsPrice?.benefits?.referralBenefits
                                                        ?.cashBackToReferralOnFirstTxn &&
                                                    unitsPrice?.benefits?.referralBenefits
                                                        ?.cashBackToReferralOnFirstTxn > 0 ? (
                                                        <div className="flex h-[38px] items-center justify-between">
                                                            <span className="flex items-center gap-1 text-sm text-neutral-500">
                                                                Cash back
                                                                <div className="group relative">
                                                                    <Info
                                                                        width="16"
                                                                        height="16"
                                                                        className="text-neutral-300"
                                                                    />
                                                                    <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                                        You will receive{" "}
                                                                        <b>
                                                                            {formatIndianCurrency(
                                                                                unitsPrice?.benefits
                                                                                    ?.referralBenefits
                                                                                    ?.cashBackToReferralOnFirstTxn,
                                                                            )}
                                                                            /- Amazon
                                                                            <br />
                                                                            voucher
                                                                        </b>{" "}
                                                                        with in 30 days of order
                                                                        <br />
                                                                        confirmation
                                                                    </div>
                                                                </div>
                                                            </span>
                                                            <span className="text-sm font-regular leading-[160%] tracking-[0.14px]">
                                                                {formatIndianCurrency(
                                                                    unitsPrice?.benefits
                                                                        ?.referralBenefits
                                                                        ?.cashBackToReferralOnFirstTxn,
                                                                )}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                    <div className="flex h-[38px] items-center justify-between">
                                                        <span className="text-sm text-neutral-500">
                                                            Total Investment amount
                                                        </span>
                                                        <span className="text-lg font-semibold leading-[160%] tracking-[0.14px]">
                                                            {units >= (minUnits ?? 0)
                                                                ? formatIndianCurrency(
                                                                      unitsPrice?.benefits?.total
                                                                          ?.withBenefitAmount ?? 0,
                                                                  )
                                                                : "₹0"}
                                                        </span>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                    {stockDetails?.shareState &&
                                    BLOCKED_SHARE_STATES.has(
                                        stockDetails.shareState.toLowerCase().trim(),
                                    ) ? (
                                        <div className="px-s9 pb-s6">
                                            <div className="rounded-lg border border-brand-primary-200 bg-[#feefea] px-3 py-3 text-center text-sm font-medium leading-[160%] tracking-[1%] text-brand-primary-600">
                                                {`The company is in ${ProductVisibility[stockDetails.shareState.toLowerCase().trim() as keyof typeof ProductVisibility]} stage; transactions are not allowed!`}
                                            </div>
                                            {!isAuthenticated ? (
                                                <RegularButton
                                                    noIcon={true}
                                                    label={"Explore other shares"}
                                                    showRightIcon={false}
                                                    variant={ButtonVariant.PrimaryDefault}
                                                    size={ButtonSize.Medium}
                                                    className="mt-2 min-w-full max-w-full flex-1"
                                                    onClick={() =>
                                                        router.push(DirectPaths.UNLISTED_SHARES)
                                                    }
                                                />
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div className="px-s9 pb-s6">
                                            {stockDetails?.isProductAvailable && (
                                                <RegularButton
                                                    ref={txnBtn}
                                                    noIcon={
                                                        userData
                                                            ? !isAllDataFetched
                                                                ? false
                                                                : !isPurchaseStart
                                                            : true
                                                    }
                                                    icon={
                                                        userData ? (
                                                            isPurchaseStart || !isAllDataFetched ? (
                                                                <LoadingIcon className="animate-spin" />
                                                            ) : (
                                                                <></>
                                                            )
                                                        ) : (
                                                            <></>
                                                        )
                                                    }
                                                    label={
                                                        !userData
                                                            ? getBtnTxt()
                                                            : isPurchaseStart
                                                              ? ""
                                                              : isAllDataFetched
                                                                ? getBtnTxt()
                                                                : ""
                                                    }
                                                    showRightIcon={false}
                                                    disabled={
                                                        !userData
                                                            ? false
                                                            : isPurchaseStart ||
                                                                !isAllDataFetched ||
                                                                (unitsPrice?.amount ?? 0) <= 0
                                                              ? true
                                                              : units <
                                                                (minUnits ??
                                                                    stockDetails?.minUnits ??
                                                                    0)
                                                    }
                                                    variant={ButtonVariant.PrimaryDefault}
                                                    size={ButtonSize.Medium}
                                                    className="min-w-full max-w-full flex-1"
                                                    onClick={startTransaction}
                                                />
                                            )}
                                            {stockDetails?.ccpsDisclaimer &&
                                            stockDetails?.isProductAvailable &&
                                            stockDetails?.ccpsDisclaimer?.title ? (
                                                <p
                                                    onClick={() =>
                                                        window.open(
                                                            stockDetails?.ccpsDisclaimer?.link,
                                                            "",
                                                        )
                                                    }
                                                    className="cursor-pointer pt-s6 text-center text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-300 underline"
                                                >
                                                    {stockDetails?.ccpsDisclaimer?.title}
                                                </p>
                                            ) : (
                                                <div className="p-2"></div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {stockDetails?.isBasket && !isMobile ? (
                                    <BasketBreakup BasketData={basketBreakup} />
                                ) : null}
                                <div className="!mb-14 mt-0 flex items-center justify-between gap-2 rounded-2xl border border-gray-200 p-s7 sm:mt-4 md:!mb-0">
                                    <span className="text-base font-medium text-black">
                                        Download the app
                                    </span>
                                    <div className="flex gap-4">
                                        <button
                                            // onClick={() =>
                                            //     window.open(
                                            //         process.env.ICM_ANDROID_APP_URL || "",
                                            //         "_blank",
                                            //     )
                                            // }
                                            onClick={() =>
                                                window.open(
                                                    process.env.APP_BRANCH_LINK_ASSETS,
                                                    "_blank",
                                                )
                                            }
                                            className="rounded-full border p-2 hover:bg-neutral-200"
                                        >
                                            <PlayStore className="h-5 w-5" />
                                        </button>
                                        <button
                                            // onClick={() =>
                                            //     window.open(process.env.ICM_APPLE_APP_URL || "")
                                            // }
                                            onClick={() =>
                                                window.open(
                                                    process.env.APP_BRANCH_LINK_ASSETS,
                                                    "_blank",
                                                )
                                            }
                                            className="rounded-full border p-2 hover:bg-neutral-200"
                                        >
                                            <AppStore className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {openStaggeringTable && (
                        <StaggeringPrice
                            pricingTiers={staggeringPrices}
                            onClose={() => setopenStaggeringTable(false)}
                        />
                    )}
                    {stockDetails && !isPurchaseStart && !mobileTableOpened ? (
                        <ScrollableButton
                            isAuthenticated={isAuthenticated}
                            txnRef={txnBtn}
                            stockDetails={stockDetails}
                            units={units}
                            startTransaction={startTransaction}
                            isPurchaseStart={isPurchaseStart}
                            getBtnTxt={getBtnTxt}
                            decreaseUnits={decreaseUnits}
                            handleUnitsChange={handleUnitsChange}
                            handleIncreaseUnits={handleIncreaseUnits}
                            userData={userData}
                            unitsPrice={unitsPrice ?? undefined}
                            setUnits={setUnits}
                            isAllDataFetched={isAllDataFetched}
                            staggeringPrices={staggeringPrices}
                            setopenStaggeringTable={setopenStaggeringTable}
                            setisMobileWidgetOpen={setisMobileWidgetOpen}
                        />
                    ) : null}
                    {isTxnJourney && stockDetails && isPurchaseStart ? (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black opacity-5">
                            <TransactionFlow
                                isin={stockDetails.ISIN}
                                thirdPartyData={thirdPartyData}
                                units={units}
                                onComplete={handleComplete}
                                onClose={handleComplete}
                                restartFlow={startTransaction}
                                userNotInvested={userNotInvested}
                                popularStocks={popularStocks?.data ?? []}
                                isRepeatTxn={
                                    newUserStates?.data?.state === IntentUserStates.RP_TXN ||
                                    newUserStates?.data?.state === IntentUserStates.TXN
                                }
                            />
                        </div>
                    ) : null}
                    {isError && <ErrorModal errorStr={errorStr ?? ""} onClose={setisError} />}
                    {isSuccess && (
                        <SuccessModal successStr={successStr ?? ""} onClose={setisSuccess} />
                    )}
                    {PendingOrdersPopup && pendingOrders && pendingOrders.length > 0 && (
                        <PendingTransactionWeb
                            onClose={handlePendingOrdersPopup}
                            cancelPendingOrders={cancelPendingOrder}
                            pendingOrders={pendingOrders}
                        />
                    )}

                    {cancelOrdersConfirmation && (
                        <CancelOrdersConfirmationModal
                            onClose={closeCancelOrdersConfirmationModal}
                        />
                    )}

                    {isNewUserStateFetched && newUserStates ? (
                        <BackNavigationHandler
                            txnCount={isPopularStock?.lastMonthPurchaseCount ?? 0}
                            isNewUser={newUserStates?.data?.state === "kyc"}
                            render1stTxnPopup={Boolean(
                                isKycDataFetched &&
                                    newUserStates?.data?.state === "kyc" &&
                                    kycErrors?.data[0]?.rejectRemarks.length === 0,
                            )}
                            renderTxnBackPopup={Boolean(
                                isPopularStock && isTxnDataFetched && !txnData?.data,
                            )}
                        />
                    ) : null}

                    {riskConsent && (
                        <HighRiskConsent
                            riskContent={stockDetails?.riskCategory?.text ?? ""}
                            product={stockDetails?.product ?? ""}
                            setriskConsent={setriskConsent}
                            setconsentAccepted={setconsentAccepted}
                            setisMobileWidgetOpen={setisMobileWidgetOpen}
                            onClosePopup={() => {
                                setAnalyticsEvent("", `US_Transaction_Risk_Close`);
                                cleverTapTrackEvent(`US_Transaction_Risk_Close`, {});
                                // initBranch()
                                //     .then(() => {
                                //         trackEvent(`US_Transaction_Risk_Close`);
                                //     })
                                //     .catch((error) =>
                                //         console.error("Error initializing Branch:", error),
                                //     );
                                setriskConsent(false);
                            }}
                        />
                    )}
                </>
            ) : (
                <div className="flex h-screen items-center justify-center">
                    <AssetpageSkeleton />
                </div>
            )}
        </>
    );
}

export default function PageClientContent(props: PageClientContentProps) {
    return (
        <TransactionFlowProvider>
            <PageContentInner {...props} />
        </TransactionFlowProvider>
    );
}
